function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe, video[controls], audio[controls], model-viewer"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

// Atelier: the Tab stops a trapped container shows at this moment: enabled, not tabindex -1, laid out and not
// visibility:hidden; a radio group counts once (its checked radio, else its first). A playing video's controls count
// (the lightbox): WebKit reports tabIndex -1 for <video controls> though Tab reaches it, so only a written tabindex
// can take it out.
function getTabStops(container) {
  return getFocusableElements(container).filter((el) => {
    const media = el.matches('video[controls], audio[controls]') && !el.hasAttribute('tabindex');
    if ((el.tabIndex < 0 && !media) || el.disabled || !el.getClientRects().length || getComputedStyle(el).visibility === 'hidden')
      return false;
    // Inside a closed <details> (other than its own summary) or an inert part: the browser keeps boxes for a closed
    // accordion's links (the phone menu's), but they cannot take focus, and Tab stuck on the first accordion.
    if (el.closest('[inert]')) return false;
    for (let d = el.closest('details:not([open])'); d; d = d.parentElement?.closest('details:not([open])'))
      if (el.parentElement !== d || el.tagName !== 'SUMMARY') return false;
    if (el.type !== 'radio' || el.checked) return true;
    const group = container.querySelectorAll(`input[type="radio"][name="${CSS.escape(el.name)}"]`);
    return group[0] === el && !Array.from(group).some((radio) => radio.checked);
  });
}

// Atelier: while a trap is on, Tab and Shift+Tab move among the container's Tab stops as they are when the key is
// pressed, and wrap at either end. Dawn picked the first and last once, when the trap started, and only wrapped at
// those two: a control hidden since (the phone menu's Search row while its field is in use) was never reached, so
// Tab ran on to the page behind the drawer; WebKit, which skips links and buttons on Tab, left from any field.
function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();

  trapFocusHandlers.keydown = function (event) {
    if (event.key !== 'Tab' || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target;
    if (target !== container && !container.contains(target)) return;
    const stops = getTabStops(container);
    if (!stops.length) return;
    const index = stops.indexOf(target);
    let next;
    if (index >= 0) next = stops[(index + (event.shiftKey ? stops.length - 1 : 1)) % stops.length];
    else {
      // Focus is on something inside that is not a stop (the container itself, a scrolling panel): the nearest stop
      // after or before it.
      const after = stops.filter((el) => target.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
      next = event.shiftKey ? stops[stops.length - after.length - 1] || stops[stops.length - 1] : after[0] || stops[0];
    }
    event.preventDefault();
    // A stop that turns out not to take focus is passed over, so Tab never sticks.
    const step = event.shiftKey ? stops.length - 1 : 1;
    for (let i = 0, at = stops.indexOf(next); i < stops.length; i++, at = (at + step) % stops.length) {
      stops[at].focus();
      if (document.activeElement === stops[at]) break;
    }
  };

  document.addEventListener('keydown', trapFocusHandlers.keydown);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus({ preventScroll: true });
}

// Atelier: while a modal overlay is open (phone menu, filter and sort sheet, currency sheet, bag drawer, size guide,
// photo lightbox) the rest of the page is inert: out of the Tab order, untappable and out of the accessibility tree,
// so VoiceOver's swipe and a screen reader's reading cursor stay in the overlay (aria-modal alone is not honoured
// everywhere). `keep` is the overlay's outermost element; every element beside it or beside one of its ancestors
// becomes inert, and aria-hidden as well: iOS 26 VoiceOver still reached images inside an inert footer (the payment
// marks), which aria-hidden removes. One overlay at a time: opening one releases the last. Only what was set here is
// released (an element that was inert or aria-hidden already stays so). Each overlay releases in its close path before
// focus goes back to its opener (an inert element cannot take focus) and when it is removed while open. `state` is
// { isOpen, close }: the listeners below run the overlay's own close when a section re-render or a page restored from
// the back-forward cache finds the page still set aside for an overlay that is not open, and release the page when the
// overlay is gone.
const pageInert = { keep: null, set: [], state: null, observer: null, covered: null, focus: null };

// Third-party layers that open over everything by design (Klaviyo's sign-up forms, Shopify's cookie banner) stay
// usable: made inert, a popup that arrived while the bag or size guide was open showed but could not be used, and a
// tester's iPad was left with a layer over Checkout (26 Sep). Klaviyo can render into an unclassed wrapper, and after
// the page was made inert, so a layer that holds such a form, or gains one later, is released too.
const OVERLAY_LAYER = '#shopify-pc__banner, [class*="kl-private-reset-css"], .klaviyo-form, [id^="klaviyo"]';
function isOverlayLayer(el) {
  return el.matches(OVERLAY_LAYER) || !!el.querySelector(OVERLAY_LAYER);
}

// Klaviyo keeps its own timing. While its form covers a theme dialog, set that dialog aside too; when the form
// leaves, restore its last control and Tab loop so Escape reaches the still-open guide, viewer or bag again.
const KLAVIYO_DIALOG = '[role="dialog"][aria-modal="true"][class*="kl-private-reset-css"]';
function syncOverlayFocus() {
  const { keep, state } = pageInert;
  if (!keep || !keep.isConnected || (state && !state.isOpen())) return;
  const popup = [...document.querySelectorAll(KLAVIYO_DIALOG)].find((el) => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
  if (popup && !pageInert.covered) {
    pageInert.covered = { inert: keep.inert, hidden: keep.getAttribute('aria-hidden') };
    keep.inert = true;
    keep.setAttribute('aria-hidden', 'true');
    removeTrapFocus();
  } else if (!popup && pageInert.covered) {
    uncoverOverlay();
    const back = pageInert.focus;
    trapFocus(keep, back?.isConnected && keep.contains(back) ? back : getTabStops(keep)[0] || keep);
  }
}

function uncoverOverlay() {
  const { keep, covered } = pageInert;
  if (!keep || !covered) return;
  keep.inert = covered.inert;
  if (covered.hidden === null) keep.removeAttribute('aria-hidden');
  else keep.setAttribute('aria-hidden', covered.hidden);
  pageInert.covered = null;
}

document.addEventListener('focusin', (event) => {
  if (pageInert.keep?.contains(event.target)) pageInert.focus = event.target;
});

// HelpfulCrowd listens for Escape on document even while covered. Dismiss only the top form through its own close
// button, and consume the matching keyup so that one press cannot also dismiss the newly uncovered theme dialog.
let overlayEscape = false;
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !pageInert.covered) return;
  const popup = [...document.querySelectorAll(KLAVIYO_DIALOG)].find((el) => el.getClientRects().length);
  const close = popup?.querySelector('button[aria-label="Close dialog"]');
  if (!close) return;
  overlayEscape = true;
  event.preventDefault();
  event.stopImmediatePropagation();
  close.click();
}, true);
document.addEventListener('keyup', (event) => {
  if (event.key !== 'Escape' || !overlayEscape) return;
  overlayEscape = false;
  event.preventDefault();
  event.stopImmediatePropagation();
}, true);

function inertPage(keep, state = null) {
  releasePage();
  pageInert.keep = keep;
  pageInert.state = state;
  pageInert.focus = keep.contains(document.activeElement) ? document.activeElement : null;
  for (let node = keep; node.parentElement && node !== document.body; node = node.parentElement) {
    for (const el of node.parentElement.children) {
      if (el === node || el.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(el.tagName)) continue;
      if (isOverlayLayer(el)) continue;
      const hide = !el.hasAttribute('aria-hidden');
      el.inert = true;
      if (hide) el.setAttribute('aria-hidden', 'true');
      pageInert.set.push({ el, hide });
    }
  }
  pageInert.observer = new MutationObserver((changes) => {
    if (!changes.some((change) => change.type === 'childList' || change.target.matches(KLAVIYO_DIALOG))) return;
    pageInert.set = pageInert.set.filter(({ el, hide }) => {
      if (!isOverlayLayer(el)) return true;
      el.inert = false;
      if (hide) el.removeAttribute('aria-hidden');
      return false;
    });
    syncOverlayFocus();
  });
  pageInert.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
  syncOverlayFocus();
}

function releasePage(keep) {
  if (keep && keep !== pageInert.keep) return;
  pageInert.observer?.disconnect();
  pageInert.observer = null;
  uncoverOverlay();
  pageInert.set.forEach(({ el, hide }) => {
    el.inert = false;
    if (hide) el.removeAttribute('aria-hidden');
  });
  pageInert.keep = null;
  pageInert.set = [];
  pageInert.state = null;
  pageInert.focus = null;
}

['shopify:section:load', 'pageshow'].forEach((type) =>
  window.addEventListener(type, () => {
    const { keep, state } = pageInert;
    if (!keep) return;
    if (keep.isConnected && state && !state.isOpen()) state.close();
    if (!keep.isConnected || (state && !state.isOpen())) releasePage(keep);
  })
);

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Dawn's "Shopify Common JS" (Shopify.bind, setSelectorByValue, addListener, postLink, CountryProvinceSelector) is
 * gone: it served the classic customer address forms, which this theme does not have (new customer accounts, no
 * templates/customers), and no theme file or app script on the store calls it (checked 25 Sep 2026: every script on
 * the home, collection, product, cart, FAQ, search and returns-portal pages, Klaviyo, HelpfulCrowd, Return Prime and
 * Shopify's own). window.Shopify itself is still created by content_for_header.
 */

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.mainDetailsToggle.addEventListener('toggle', () => this.syncSheetState(this.mainDetailsToggle.open));
    this.syncSheetState(this.mainDetailsToggle.open);

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  connectedCallback() {
    // A tap before global.js arrived opened native <details>, without the slide, focus trap or body lock. Reconcile
    // after construction, when HeaderDrawer's own fields are ready, through the same path as an ordinary opening.
    if (this.mainDetailsToggle.open) this.openMenuDrawer(this.mainDetailsToggle.querySelector('summary'));
  }

  // facets.js calls this again after every filter update. The bound handlers are made once, so controls that survive
  // the re-render are not given a second copy (addEventListener ignores a listener it already has).
  // Atelier: the sub-menu close handler goes on Dawn's sub-menu back buttons only. Dawn bound it to every <button> in
  // the drawer, so the phone menu's search arrow half-closed the menu on its way to /search (open and the slide gone,
  // but the page still inert and scroll-locked, as the back-forward cache then brought it back); the drawers' own
  // close buttons already close through the summary (closeMenuDrawer).
  bindEvents() {
    this.boundSummaryClick ||= this.onSummaryClick.bind(this);
    this.boundCloseButtonClick ||= this.onCloseButtonClick.bind(this);
    this.querySelectorAll('summary').forEach((summary) => summary.addEventListener('click', this.boundSummaryClick));
    this.querySelectorAll('.menu-drawer__close-button, .mobile-facets__close-button').forEach((button) =>
      button.addEventListener('click', this.boundCloseButtonClick)
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      event.preventDefault();
      // The open attribute stays through the closing slide. A new tap reverses that slide, not another close.
      if (isOpen && !this.closeFrame) this.closeMenuDrawer(event, summaryElement);
      else {
        cancelAnimationFrame(this.closeFrame);
        this.closeFrame = null;
        detailsElement.open = true;
        this.openMenuDrawer(summaryElement);
      }

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    this.syncSheetState(true);
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    this.openModal(summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  // Atelier: global.js makes Filter operable before facets.js arrives. Keep the sheet above the header and chat
  // hidden from that first open through the close animation, including a <details> already open at upgrade.
  syncSheetState(open) {
    if (!this.mainDetailsToggle.matches('.mobile-facets__disclosure')) return;
    this.closest('.at-mfilters')?.classList.toggle('is-open', open);
    document.body.classList.toggle('at-fsheet-open', open);
  }

  // Atelier: an open drawer or sheet with a [role=dialog] (the phone menu, the phone filter and sort sheet) is modal:
  // the page around it is inert (inertPage), its summary, which lies under the backdrop (and is the backdrop a tap
  // closes the filter sheet with), leaves the Tab order and the accessibility tree, and focus starts in the dialog on
  // its first button (the X) once the <details> has opened: a closed one's content cannot take focus, and focus
  // already inside (Sort puts it on the order in use) stays. Without a dialog, Dawn's way: focus stays on the summary.
  openModal(summaryElement) {
    const details = this.mainDetailsToggle;
    const dialog = details.querySelector('[role="dialog"]');
    if (!dialog) return trapFocus(details, summaryElement);
    summaryElement.setAttribute('aria-hidden', 'true');
    summaryElement.setAttribute('tabindex', '-1');
    inertPage(this, { isOpen: () => details.hasAttribute('open'), close: () => this.reset() });
    // Hidden by its breakpoint while open (a phone turned sideways, 844px wide, hides the filter sheet; from 1200px the
    // menu), it closes for good rather than leave the page set aside and scroll-locked behind nothing.
    const query = { tablet: '(max-width: 1199px)', mobile: '(max-width: 800px)' }[this.dataset.breakpoint];
    if (query) {
      this.shown = window.matchMedia(query);
      this.onShown ||= (e) => !e.matches && this.reset();
      this.shown.addEventListener('change', this.onShown);
    }
    setTimeout(() => {
      if (!details.hasAttribute('open') || summaryElement.getAttribute('aria-hidden') !== 'true') return;
      const active = document.activeElement;
      trapFocus(details, dialog.contains(active) ? active : dialog.querySelector('button') || dialog);
    });
  }

  // Atelier: the page and the summary back, before focus returns to the summary.
  closeModal() {
    this.shown?.removeEventListener('change', this.onShown);
    this.shown = null;
    releasePage(this);
    const summary = this.mainDetailsToggle?.querySelector('summary');
    if (summary?.getAttribute('aria-hidden') !== 'true') return;
    summary.removeAttribute('aria-hidden');
    summary.removeAttribute('tabindex');
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.closeModal();
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  // Focus moved to something outside the open drawer (a screen reader's cursor, another script, the header search):
  // the drawer closes and focus stays where it went. A press on the drawer's own blank space leaves focus on <body>,
  // or, in Safari, on the nearest focusable element holding the drawer (a tap on a filter chip's label in the phone
  // filter sheet focused <main tabindex=-1> and closed the sheet under the tap); neither counts, nor does anything
  // while the drawer is already closing. (Dawn called closeMenuDrawer() without an event, which returned at once, so a
  // drawer left behind focus stayed open and scroll-locked.)
  onFocusOut(event) {
    setTimeout(() => {
      const details = this.mainDetailsToggle;
      const active = document.activeElement;
      if (!details.hasAttribute('open') || !details.classList.contains('menu-opening')) return;
      if (!active || active.contains(details) || details.contains(active)) return;
      this.closeMenuDrawer(event);
      details.querySelector('summary').setAttribute('aria-expanded', false);
    });
  }

  // Atelier: removed while open (the theme editor re-renders or moves its section): the body lock, the focus trap and
  // the open state go with it, or the preview stayed scroll-locked with Tab held by a trap on a detached drawer.
  disconnectedCallback() {
    if (this.mainDetailsToggle?.hasAttribute('open') || pageInert.keep === this) this.reset();
  }

  // Atelier: closes at once, with no slide and no focus move, and undoes everything an open drawer set (open state, body
  // lock, focus trap, inert page, hidden summary): removed while open, hidden by its breakpoint, or found not open in a
  // page restored from the back-forward cache (inertPage's listeners).
  reset() {
    const details = this.mainDetailsToggle;
    if (!details) return;
    cancelAnimationFrame(this.closeFrame);
    this.closeFrame = null;
    if (details.hasAttribute('open') || pageInert.keep === this) removeTrapFocus();
    details.removeAttribute('open');
    this.syncSheetState(false);
    details.classList.remove('menu-opening');
    details.querySelector('summary')?.setAttribute('aria-expanded', false);
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    this.closeModal();
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    // Atelier: a back button on the drawer itself closes the whole drawer, the one way that undoes all of it.
    if (detailsElement === this.mainDetailsToggle) return this.closeMenuDrawer(event, detailsElement.querySelector('summary'));
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;
    const main = detailsElement === this.mainDetailsToggle;
    if (main) cancelAnimationFrame(this.closeFrame);

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        const frame = window.requestAnimationFrame(handleAnimation);
        if (main) this.closeFrame = frame;
      } else {
        detailsElement.removeAttribute('open');
        if (main) {
          this.closeFrame = null;
          this.syncSheetState(false);
        }
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    const frame = window.requestAnimationFrame(handleAnimation);
    if (main) this.closeFrame = frame;
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    this.openModal(summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  // Atelier: also without elementToFocus (the focus-out close leaves focus where it went); Dawn returned then.
  closeMenuDrawer(event, elementToFocus) {
    if (event === undefined) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header?.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  reset() {
    super.reset();
    this.header?.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        // Atelier: the product lightbox (data-at-lightbox) zooms on a photo click and closes on the paper itself
        // (assets/at-lightbox.js), so a mouse pointerup anywhere must not close it.
        if (this.hasAttribute('data-at-lightbox')) return;
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    // Atelier: the page behind the size guide or the photo lightbox is inert while it is open
    inertPage(this, { isOpen: () => this.hasAttribute('open'), close: () => this.hide() });
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    releasePage(this);
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    // Atelier: slides that are not side by side (stacked, e.g. before the slider sheet applies) have no offset, which
    // made the counter "NaN / -Infinity". They count as one page until they are laid out in a row.
    if (!(this.sliderItemOffset > 0)) {
      this.slidesPerPage = this.sliderItemsToShow.length;
      this.totalPages = 1;
      this.update();
      return;
    }
    this.slidesPerPage = Math.max(
      1,
      Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset)
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = this.sliderItemOffset > 0 ? Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1 : 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.dispatchProductSelectEvent();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getAllSelectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
      if (checked) {
        options.push({ name: checked.dataset.optionName || '', value: checked.value });
      }
    });
    return options;
  }

  dispatchProductSelectEvent() {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent) return;

    const deferred = ProductSelectEvent.createPromise();
    this.pendingSelectPromise = deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred = this.pendingSelectPromise;
    this.pendingSelectPromise = null;
    return deferred;
  }

  resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
    const deferred = this.takePendingSelectPromise();
    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: variant.available,
          price: {
            amount: sourceVariantSelects?.dataset.selectedPriceAmount,
            currencyCode: sourceVariantSelects?.dataset.currencyCode,
          },
          selectedOptions: this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({ variant: null });
    }
  }

  rejectPendingSelectPromise(error) {
    this.takePendingSelectPromise()?.reject(error);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      // Atelier: 1200px (about 1.5 phone viewports) instead of Dawn's 400px, so the reserved slot (at-product.css
      // product-recommendations:empty) is filled before the shopper reaches it.
      { rootMargin: '0px 0px 1200px 0px' }
    );
    this.observer.observe(this);
  }

  // Atelier: one failed request (a dropped connection; the dev proxy's 404 bursts) tries once more before the slot
  // collapses, so a single miss no longer closes the reserved room and moves everything below it. No timer: at once.
  // Idempotent per product: the scroll observer and at-warmup.js (which starts it early) both call this, and a second
  // call while the first is in flight or after it landed returns the same promise instead of fetching again and
  // replacing the rail (that reset a phone shopper's swipe position and dropped a focused card).
  loadRecommendations(productId) {
    if (this.recsFor === productId && this.recsLoad) return this.recsLoad;
    this.recsFor = productId;
    this.observer?.unobserve(this);
    return (this.recsLoad = this.fetchRecommendations(productId, false));
  }

  fetchRecommendations(productId, last) {
    return fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => {
        // Atelier: a failed response (the dev proxy 404s this endpoint now and then) carries an error page, not cards.
        // Treat it like "no recommendations" so the slot collapses instead of reading the error page's markup.
        if (!response.ok) throw new Error(`Recommendations ${response.status}`);
        return response.text();
      })
      .then((text) => {
        // Atelier: parse into an inert document. A detached <div> in this document starts fetching every card image
        // (lazy or not) before the markup is inserted, so each photo was requested twice.
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = html.querySelector('product-recommendations');
        const content = this.hasAttribute('data-at-recent')
          ? recommendations?.querySelector('[data-at-recommendations]')
          : recommendations;

        if (content?.innerHTML.trim().length) {
          this.innerHTML = this.hasAttribute('data-at-recent') ? content.outerHTML : content.innerHTML;
          this.dataset.recommendationsState = 'ready';
          this.dispatchEvent(new Event('at:recommendations-ready'));
          // The injected cards carry empty HelpfulCrowd rating slots; ask the store script to fill them (no-op if absent).
          if (typeof window.atRefreshReviews === 'function') window.atRefreshReviews();
        } else {
          this.collapse();
        }


        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        if (!last) return this.fetchRecommendations(productId, true);
        console.error(e);
        this.collapse();
      });
  }

  // Atelier: no Shopify cards (or the request failed). Let saved cards fill the rail first; otherwise Dawn's .hidden
  // removes both the loading reserve and section padding, leaving no blank paper above the next section.
  collapse() {
    this.dataset.recommendationsState = 'empty';
    // Saved cards can keep the Atelier rail open even when Shopify has no suggestions.
    this.dispatchEvent(new Event('at:recommendations-ready'));
    if (this.hasAttribute('data-at-recent') && this.querySelector('.at-card')) return;
    this.classList.add('is-empty', 'hidden');
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
